import { appConfig } from "../authConfig";
import { decodeGraphCredentialId } from "../utils/graphServiceUtils";

const baseURl = appConfig.apiBaseUrl;
/**
 * Create WebAuthn credential using browser's Credential Management API
 * @param {Object} creationOptions - WebAuthn creation options
 * @returns {Promise<PublicKeyCredential>} - Created credential
 */
export async function createCredential(creationOptions) {
  creationOptions.excludeCredentials = creationOptions.excludeCredentials.map(
    (c) => ({
      ...c,
      id: decodeGraphCredentialId(c.id),
    }),
  );
  const authenticatorSelection = creationOptions.authenticatorSelection;
  authenticatorSelection.authenticatorAttachment = undefined;
  const publicKey = {
    challenge: creationOptions.challenge,
    rp: {
      id: appConfig.customDomain || creationOptions.rp.id,
      name: creationOptions.rp.name,
    },
    user: {
      id: creationOptions.user.id,
      name: creationOptions.user.name,
      displayName: creationOptions.user.displayName,
    },
    pubKeyCredParams: creationOptions.pubKeyCredParams,
    excludeCredentials: creationOptions.excludeCredentials,
    timeout: creationOptions.timeout,
    authenticatorSelection: authenticatorSelection,
    attestation: creationOptions.attestation,
  };

  console.log("Passkey creation options configured", publicKey);
  try {
    const credential = await navigator.credentials.create({ publicKey });
    console.log("Passkey credential created successfully");
    return credential;
  } catch (error) {
    console.error("Error creating passkey credential:", error);
    throw error;
  }
}

/**
 * Make a request to the API with standardized error handling
 * @param {string} endpoint - API endpoint path (e.g., '/users/123/authentication/fido2Methods')
 * @param {Object} options - Fetch options object
 * @param {string} options.method - HTTP method (GET, POST, DELETE, etc.)
 * @param {Object} options.headers - Additional headers
 * @param {string} options.body - Request body (JSON string)
 * @param {string} userToken - Bearer token for authentication
 * @returns {Promise<Response>} - Fetch response object
 * @throws {Error} - Formatted error if request fails
 */
async function makeApiRequest(endpoint, options = {}, userToken) {
  const defaultHeaders = {
    "Content-Type": "application/json",
    "Accept-Encoding": "gzip, deflate, br",
  };

  // Add authorization header if token provided
  if (userToken) {
    defaultHeaders.Authorization = `Bearer ${userToken}`;
  }

  const requestOptions = {
    method: "GET",
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  // console.log("Making API request:", baseURl, appConfig);

  const response = await fetch(`${baseURl}${endpoint}`, requestOptions);

  if (!response.ok) {
    const textError = await response.text();
    throw new Error(`HTTP ${response.status}: ${textError}`);
  }

  return response;
}

/**
 * Make a GET request to the API
 * @param {string} endpoint - API endpoint path
 * @param {string} userToken - Bearer token for authentication
 * @param {Object} headers - Additional headers
 * @returns {Promise<Response>} - Fetch response object
 */
async function apiGet(endpoint, userToken, headers = {}) {
  return makeApiRequest(endpoint, { method: "GET", headers }, userToken);
}

/**
 * Make a POST request to the API
 * @param {string} endpoint - API endpoint path
 * @param {Object} body - Request body object
 * @param {string} userToken - Bearer token for authentication
 * @param {Object} headers - Additional headers
 * @returns {Promise<Response>} - Fetch response object
 */
async function apiPost(endpoint, body, userToken, headers = {}) {
  return makeApiRequest(
    endpoint,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers,
    },
    userToken,
  );
}

/**
 * Make a DELETE request to the API
 * @param {string} endpoint - API endpoint path
 * @param {string} userToken - Bearer token for authentication
 * @param {Object} headers - Additional headers
 * @returns {Promise<Response>} - Fetch response object
 */
async function apiDelete(endpoint, userToken, headers = {}) {
  return makeApiRequest(endpoint, { method: "DELETE", headers }, userToken);
}

// All API Endpoints
export async function fetchUserPasskeys(userToken, userId) {
  const response = await apiGet(`/api/accounts/passkeys/${userId}`, userToken);
  // console.log("fetchUseraccounts/passkeys response:", typeof response, response);
  return await response.json();
}

export async function getPasskeyCreationOptions(userToken, userId) {
  const response = await apiGet(
    `/api/accounts/passkeys/${userId}/options`,
    userToken,
  );
  return await response.json();
}

export async function registerUserPasskey(creationOptions, userToken, userId) {
  const response = await apiPost(
    `/api/accounts/passkeys/${userId}`,
    creationOptions,
    userToken,
  );
}

export async function deleteUserPasskey(userToken, userId, passkeyId) {
  const response = await apiDelete(
    `/api/accounts/passkeys/${userId}?passkeyId=${passkeyId}`,
    userToken,
  );
}
